import { useQueries } from '@tanstack/react-query';
import { useOperator } from '@/auth/use-operator';
import { getLockState } from '@/lib/locks/api';
import type { LockState } from '@/lib/locks/types';
import type { GroupLock } from '@/components/allocation/types';

// Per-group (sub-case) lock state for the assignment board. One query per group
// keyed `['cases', subCaseUrn, 'lock']` — the SAME key each per-group
// <LockIndicator/> uses, so they share the cache (one fetch per group) and the
// central applyEvent merge (StateChanged) keeps them all live. The board reads
// this to gate writes: a group's assign / hold / confirm is enabled only when
// the operator holds that group's lock.

// GroupLock moved to the reusable allocation family; re-exported for back-compat.
export type { GroupLock } from '@/components/allocation/types';

const UNLOCKED: GroupLock = { isMine: false, isHeld: false, holderName: null, expiresAt: null };

export function useGroupLocks(subCaseUrns: string[]): (urn: string) => GroupLock {
  const { data: operator } = useOperator();
  const operatorUrn = operator?.operatorUrn ?? null;

  const results = useQueries({
    queries: subCaseUrns.map((urn) => ({
      queryKey: ['cases', urn, 'lock'] as const,
      queryFn: () => getLockState(urn),
      staleTime: 0,
      refetchOnWindowFocus: true,
    })),
  });

  const byUrn = new Map<string, GroupLock>();
  subCaseUrns.forEach((urn, i) => {
    const state = results[i]?.data as LockState | undefined;
    const holder = state?.holder ?? null;
    byUrn.set(urn, {
      isMine: !!holder && operatorUrn !== null && holder.operatorUrn === operatorUrn,
      isHeld: holder !== null,
      holderName: holder?.displayName ?? null,
      expiresAt: state?.expiresAt ?? null,
    });
  });

  return (urn: string): GroupLock => byUrn.get(urn) ?? UNLOCKED;
}
